import { clamp } from "verdi-common/utils/mathUtils";
import {
    alwaysFullWidthCardTypes,
    cardTypeFullWidthBreakpoints,
    doesCardTypeHideChips,
} from "../card-routing/card-config";
import { INFO_CARD_TYPE } from "../card-routing/card-types";
import { getDesktopCardMaxWidthPx } from "./card-widths";

// Card dimensions
export const CARD_ABSOLUTE_MAX_HEIGHT_PX = 890;
export const DESKTOP_CARD_HEIGHT_PERCENT = 80;
const DESKTOP_CARD_MIN_HEIGHT_PX = 0;
export const MOBILE_CARD_HEIGHT_PERCENT = 65;
const MIN_MAP_VIEW_HEIGHT_PX = 120;

/** Optional card-specific replacements for the shared height defaults. */
export interface CardHeightOptions {
    absoluteMaxHeightPx?: number;
    desktopHeightPercent?: number;
    /** Floor under `desktopHeightPercent`, so short screens get a larger share. */
    desktopMinHeightPx?: number;
    mobileHeightPercent?: number;
}

/** Gets navbar height from CSS variable with fallback */
export const getNavbarHeight = (): number => {
    const cssValue = window.getComputedStyle(document.body).getPropertyValue("--nav-bar-height");
    const parsedValue = Number((cssValue || "").replace("px", "").trim());
    return parsedValue > 0 ? parsedValue : 55;
};

/** Returns best supported viewport unit (dvh > svh > vh) */
export const getBestViewportUnit = (): "dvh" | "svh" | "vh" => {
    if (CSS.supports("height", "100dvh")) return "dvh";
    if (CSS.supports("height", "100svh")) return "svh";
    return "vh";
};

/** Determines if there is enough height above the card to meaningfully show the map */
export const hasEnoughHeightAboveCardForMap = (
    chipsHeight: number,
    { mobileHeightPercent = MOBILE_CARD_HEIGHT_PERCENT }: CardHeightOptions = {},
): boolean => {
    const navbarHeight = getNavbarHeight();
    const availableHeightAboveCard =
        window.innerHeight * ((100 - mobileHeightPercent) / 100) - chipsHeight - navbarHeight;
    return availableHeightAboveCard > MIN_MAP_VIEW_HEIGHT_PX;
};

/**
 * Returns desktop card height in pixels.
 */
const calculateDesktopCardHeight = ({
    absoluteMaxHeightPx = CARD_ABSOLUTE_MAX_HEIGHT_PX,
    desktopHeightPercent = DESKTOP_CARD_HEIGHT_PERCENT,
    desktopMinHeightPx = DESKTOP_CARD_MIN_HEIGHT_PX,
}: CardHeightOptions): number => {
    const viewportHeight = window.innerHeight;
    const desktopPercentHeight = viewportHeight * (desktopHeightPercent / 100);
    return clamp(desktopPercentHeight, desktopMinHeightPx, absoluteMaxHeightPx);
};

/**
 * Returns mobile card height in pixels when leaving space for map.
 */
const calculateMobileCardHeightWithMapSpace = ({
    absoluteMaxHeightPx = CARD_ABSOLUTE_MAX_HEIGHT_PX,
    mobileHeightPercent = MOBILE_CARD_HEIGHT_PERCENT,
}: CardHeightOptions): number => {
    const viewportHeight = window.innerHeight;
    const mobilePercentHeight = viewportHeight * (mobileHeightPercent / 100);
    return Math.min(absoluteMaxHeightPx, mobilePercentHeight);
};

/**
 * Returns mobile card height in pixels when not leaving space for map.
 * Mobile without map space: full height minus navbar
 */
const calculateMobileCardHeightWithoutMapSpace = (): number => {
    const viewportHeight = window.innerHeight;
    const navbarHeight = getNavbarHeight();
    return viewportHeight - navbarHeight;
};

/**
 * Returns card height in pixels for the current environment.
 * This is the single source of truth for card height calculations.
 */
export const calculateCardHeight = (
    isMobile: boolean,
    effectiveChipsHeight: number,
    options: CardHeightOptions = {},
): number => {
    // Desktop
    if (!isMobile) {
        return calculateDesktopCardHeight(options);
    }

    // Mobile
    if (hasEnoughHeightAboveCardForMap(effectiveChipsHeight, options)) {
        return calculateMobileCardHeightWithMapSpace(options);
    }
    return calculateMobileCardHeightWithoutMapSpace();
};

/** Returns the computed card height expression when leaving space for the map to show */
export const getCardHeightExpressionWithMapSpace = (
    viewportUnit: string,
    {
        absoluteMaxHeightPx = CARD_ABSOLUTE_MAX_HEIGHT_PX,
        mobileHeightPercent = MOBILE_CARD_HEIGHT_PERCENT,
    }: CardHeightOptions = {},
): string => `calc(min(${absoluteMaxHeightPx}px, ${mobileHeightPercent}${viewportUnit}))`;

/** Returns the computed card height expression when not leaving space for the map to show */
export const getCardHeightExpressionWithoutMapSpace = (viewportUnit: string): string =>
    `calc(100${viewportUnit} - ${getNavbarHeight()}px)`;

/**
 * Returns the desktop card height expression as a CSS string.
 * Uses the same logic as calculateDesktopCardHeight but returns a CSS expression.
 */
const getDesktopCardHeightExpression = ({
    absoluteMaxHeightPx = CARD_ABSOLUTE_MAX_HEIGHT_PX,
    desktopHeightPercent = DESKTOP_CARD_HEIGHT_PERCENT,
    desktopMinHeightPx = DESKTOP_CARD_MIN_HEIGHT_PX,
}: CardHeightOptions): string =>
    `min(${absoluteMaxHeightPx}px, max(${desktopMinHeightPx}px, ${desktopHeightPercent}vh))`;

/**
 * Returns the mobile card height expression as a CSS string.
 * Uses the same decision logic as calculateMobileCardHeight but returns a CSS expression.
 */
const getMobileCardHeightExpression = (chipsHeight: number, options: CardHeightOptions): string => {
    const viewportUnit = getBestViewportUnit();
    // Use the same decision logic as calculateMobileCardHeightPx
    if (hasEnoughHeightAboveCardForMap(chipsHeight, options)) {
        return getCardHeightExpressionWithMapSpace(viewportUnit, options);
    }
    return getCardHeightExpressionWithoutMapSpace(viewportUnit);
};

/** Returns the computed card height expression for the current environment */
export const getCardHeightExpression = (
    isMobile: boolean,
    chipsHeight: number,
    options: CardHeightOptions = {},
): string => {
    // Use the same decision logic as calculateCardHeight
    if (!isMobile) {
        return getDesktopCardHeightExpression(options);
    }
    return getMobileCardHeightExpression(chipsHeight, options);
};

/**
 * Calculates the effective chips height for card height calculations.
 *
 * References logic that controls chip visibility to know whether chips will be visible
 * when a card is open
 *
 * @param chipsHeight - The actual measured height of the chips bar
 * @param cardType - The type of card that is open, or undefined if no card is open
 * @returns The effective chips height (0 if chips will be hidden, otherwise the actual chips height)
 */
export const getEffectiveChipsHeight = (chipsHeight: number, cardType: INFO_CARD_TYPE | undefined): number =>
    doesCardTypeHideChips(cardType) ? 0 : chipsHeight;

/**
 * Returns the expected card width in pixels for a right-side card before it has been measured.
 * On mobile, cards are always full width; on desktop each card type has a defined max-width.
 */
export const calculateCardWidth = (cardType: INFO_CARD_TYPE | undefined, isMobile: boolean): number => {
    // Cards are always full screen on mobile
    if (isMobile) return window.innerWidth;
    if (cardType !== undefined && alwaysFullWidthCardTypes.has(cardType)) return window.innerWidth;

    return getDesktopCardMaxWidthPx(cardType);
};

/**
 * Returns true when this card type uses full-width layout at the current viewport width.
 */
export const isCardFullWidthLayout = (cardType: INFO_CARD_TYPE | undefined): boolean => {
    if (cardType === undefined) return false;

    // Cards in alwaysFullWidthCardTypes are unconditionally full-width
    if (alwaysFullWidthCardTypes.has(cardType)) return true;

    // Cards in cardTypeFullWidthBreakpoints are responsive full-width
    const breakpoint = cardTypeFullWidthBreakpoints[cardType];
    if (breakpoint === undefined) {
        // Card type is not registered in either full-width set — new card types must be added to
        // alwaysFullWidthCardTypes or cardTypeFullWidthBreakpoints in card-config.ts
        if (import.meta.env.DEV) {
            console.warn(
                `[card-geometry] isCardFullWidthLayout: no breakpoint registered for card type "${cardType}" — defaulting to side-anchored`,
            );
        }
        return false;
    }
    return window.matchMedia(`(max-width: ${breakpoint})`).matches;
};
